import React, { useMemo } from 'react';
import { DmsAltBox, DmsSelect, DmsTextField } from 'dmsbasereact';
import { Grid, Typography, Box } from '@mui/material';
import { RETURN_REASON_KEYS, useReturnReasons } from '../../../constants/ReturnReasons';
import { UserInstitutionSelect } from '../../UserInstitutionSelect/UserInstitutionSelect';
import { DmsBatchSearch } from '../../DmsBatchSearch/DmsBatchSearch';

export const ReturnReasonContainer = ({
    data,
    handleChange,
    handleReturnReasonChange,
    handleInstitutionChange,
    ReturnReasonOptions,
    returnReasons = [],
    selectedReason,
    fieldHasError,
    helperText,
    TreatmentDiscontinuationOptions,
    QualityIssueOptions,
    intl,
    checkSubmitted,
    institutionOptions = null,
    managementMode = false,
}) => {
    const ReturnReasonsTranslations = useReturnReasons();

    // Create options from backend returnReasons with translated labels
    // Uses reason.name (string key) as value for consistency with RETURN_REASON_KEYS
    // returnReasons prop is already filtered by institution category in useReturnForm
    const returnReasonOptions = useMemo(() => {
        if (returnReasons.length === 0) return [];

        return returnReasons.map(reason => ({
            value: reason.name,
            label: ReturnReasonsTranslations[reason.name] || reason.name
        }));
    }, [returnReasons, ReturnReasonsTranslations]);

    return (
        <DmsAltBox.Card>
            <DmsAltBox.Header>
                <Typography variant="h6">
                    {intl.get("ReturnInformation").d("Return Information")}
                </Typography>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                <Grid item xs={12}>
                    <UserInstitutionSelect
                        label={intl.get("Institution").d("Institution") + " *"}
                        value={data.requesting_organization_pk}
                        onChange={handleInstitutionChange}
                        showApprovalStatus={true}
                        required
                        error={fieldHasError("requesting_organization_pk")}
                        helperText={helperText("requesting_organization_pk")}
                        externalOptions={institutionOptions}
                        disabled={managementMode}
                    />
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <DmsSelect
                            key={`return_reason_pk-${data.return_reason_pk}-${returnReasonOptions.length}`}
                            label={intl.get("ReturnReason").d("Return Reason") + " *"}
                            name="return_reason_pk"
                            options={returnReasonOptions}
                            value={data.return_reason_pk}
                            onChange={handleReturnReasonChange}
                            fullWidth
                            error={fieldHasError("return_reason_pk")}
                            helperText={helperText("return_reason_pk")}
                        />
                    </Grid>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <DmsTextField
                            label={intl.get("reasonTextContainer").d("Reason Details")}
                            name="reason_details_text"
                            type="text"
                            value={data.reason_details_text}
                            onChange={handleChange}
                            required= {data.reason_details === "OTHER"}
                            fullWidth
                            multiline
                            rows={3}
                            error={fieldHasError("reason_details_text")}
                            helperText={helperText("reason_details_text")}
                        />
                    </Grid>
                </Box>

                {selectedReason?.name === RETURN_REASON_KEYS.TREATMENT_DISCONTINUATION && (
                    <>
                        <Grid item xs={12}>
                            <Box sx={{ mt: 2 }}>
                                <DmsSelect
                                    label={intl.get("treatmentDiscontinuationReason").d("Treatment Discontinuation Reason") + " *"}
                                    name="reason_details"
                                    options={TreatmentDiscontinuationOptions}
                                    value={data.reason_details}
                                    onChange={(value) => handleChange({ target: { name: "reason_details", value: value } })}
                                    fullWidth
                                    error={fieldHasError("reason_details")}
                                    helperText={helperText("reason_details")}
                                />
                            </Box>
                        </Grid>
                    </>
                )}

                {selectedReason?.name === RETURN_REASON_KEYS.QUALITY_ISSUE && (
                    <>
                        <Grid item xs={12}>
                            <Box sx={{ mt: 2 }}>
                                <DmsSelect
                                    label={intl.get("qualityReason").d("Quality Reason") + " *"}
                                    name="reason_details"
                                    options={QualityIssueOptions}
                                    value={data.reason_details}
                                    onChange={(value) => handleChange({ target: { name: "reason_details", value } })}
                                    fullWidth
                                    error={fieldHasError("reason_details")}
                                    helperText={helperText("reason_details")}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ mt: 2 }}>
                                <DmsBatchSearch
                                    organizationBranchPk={data.requesting_organization_pk}
                                    value={data.batch_pk}
                                    onChange={(value) => handleChange({ target: { name: "batch_pk", value } })}
                                    required
                                    error={fieldHasError("batch_pk")}
                                    helperText={helperText("batch_pk")}
                                />
                            </Box>
                        </Grid>
                    </>
                )}

                {selectedReason?.name === RETURN_REASON_KEYS.ORDER_DIVERGENCE && (
                    <Box sx={{ mt: 2 }}>
                        <Typography
                            variant="body2"
                            color={checkSubmitted ? "success.main" : "text.secondary"}
                            sx={{ mb: 1, fontWeight: checkSubmitted ? 'bold' : 'normal' }}
                        >
                            {checkSubmitted
                                ? intl.get("CheckoutCompleted").d("✓ Checkout completed successfully")
                                : intl.get("CompleteCheckout").d("Complete the checkout process first")}
                        </Typography>
                    </Box>
                )}
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};
